import { Flex, Skeleton, Table, Transfer } from 'antd';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { useState } from 'react';
import { useCustomerPropertiesQuery } from './jobsQueries';

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

interface BulkJobCopy {
  key: string
  customer: string
  customerId: number
  property: string
  propertyId: number
}

interface TableTransferProps extends TransferProps<TransferItem> {
  dataSource: BulkJobCopy[];
  leftColumns: TableColumnsType<BulkJobCopy>;
  rightColumns: TableColumnsType<BulkJobCopy>;
}

const TableTransfer: React.FC<TableTransferProps> = (props) => {
  const { leftColumns, rightColumns, ...restProps } = props;
  return (
    <Transfer style={{ width: '100%' }} {...restProps} >
      {({
        direction,
        filteredItems,
        onItemSelect,
        onItemSelectAll,
        selectedKeys: listSelectedKeys,
        disabled: listDisabled,
      }) => {
        const columns = direction === 'left' ? leftColumns : rightColumns;
        const rowSelection: TableRowSelection<TransferItem> = {
          getCheckboxProps: () => ({ disabled: listDisabled }),
          onChange(selectedRowKeys) {
            onItemSelectAll(selectedRowKeys as string[], 'replace');
          },
          selectedRowKeys: listSelectedKeys,
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        };

        return (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredItems}
            size="small"
            style={{ pointerEvents: listDisabled ? 'none' : undefined }}
            onRow={({ key, disabled: itemDisabled }) => ({
              onClick: () => {
                if (itemDisabled || listDisabled) {
                  return;
                }
                onItemSelect(key, !listSelectedKeys.includes(key));
              },
            })}
            pagination={{ hideOnSinglePage: true }}
          />
        );
      }}
    </Transfer>
  );
};

const columns: TableColumnsType<BulkJobCopy> = [
  {
    dataIndex: 'customer',
    title: 'Customer',
  },
  {
    dataIndex: 'property',
    title: 'Property',
  },
];

const filterOption = (input: string, item: BulkJobCopy) =>
  item.customer?.toLowerCase().includes(input.toLowerCase()) || item.property?.toLowerCase().includes(input.toLowerCase())

type Props = {
  onSelection: (items: SelectedCustomerProperty[]) => void
}

export type SelectedCustomerProperty = {
  customerId: number
  customer: string
  propertyId: number
  property: string
}

const JobTransferTable = ({onSelection} : Props) => {
  const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([]);

  const onChange: TableTransferProps['onChange'] = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys)
    onSelection(nextTargetKeys.map(key => customersData.find(item => item.key === key))
      .map(item => ({
        customerId: item?.customerId ?? 0, 
        propertyId: item?.propertyId ?? 0, 
        customer: item?.customer ?? '', 
        property: item?.property ?? ''})))
  };

  const customerPropertiesQuery = useCustomerPropertiesQuery() 

  const customersData: BulkJobCopy[] = customerPropertiesQuery.data?.map((item) =>
    { return {
      key: `${item.customerId}-${item.propertyId}`,
      customer: item.customerDisplayName,
      propertyId: item.propertyId,
      property: item.address1 || '(No Address)',
      customerId: item.customerId
    }
  }) || [];

  if(customerPropertiesQuery.isLoading) {
    return <Skeleton active/>
  }

  return <Flex align="start" gap="middle" vertical>
      <TableTransfer
        dataSource={customersData}
        targetKeys={targetKeys}
        showSearch
        showSelectAll={false}
        onChange={onChange}
        filterOption={filterOption}
        leftColumns={columns}
        rightColumns={columns}
        locale={{
          itemUnit: 'property',
          itemsUnit: 'properties',
          searchPlaceholder: 'Search customers and properties'
        }}
      />
    </Flex>
}

export default JobTransferTable